import { defineStore } from 'pinia';
import api from '@/services/api';

const WELCOME_MESSAGE = "Bonjour !\n\nJe suis Jarvin, votre assistant virtuel Inaubi ! J'ai été conçu spécifiquement pour les administrateurs Google Workspace et/ou LumApps. Je suis là pour vous aider et vous conseiller sur l'automatisation des actions, le pilotage de votre plateforme et l'optimisation de l'adoption de Google Workspace.\n\n Je connais par coeur la documentation GAM, LumApps, et je me débrouille pas mal sur un paquet de tâches techniques autour de Google Workspace !\n\nN'hésitez pas à me poser vos questions !";

const getInitialState = () => {
    const storedHistory = localStorage.getItem('chatHistory');
    const lastMessageTimestamp = localStorage.getItem('lastMessageTimestamp');
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    let history = [];
    
    if (storedHistory && lastMessageTimestamp && (now - parseInt(lastMessageTimestamp, 10) < twentyFourHours)) {
        try {
            history = JSON.parse(storedHistory);
            // Basic validation to ensure history is an array
            if (!Array.isArray(history)) {
                 history = null;
            }
        } catch (e) {
            console.error("Could not parse chat history from localStorage", e);
            history = null;
        }
    }

    if (!history || history.length === 0) {
         // Clear any expired or invalid data
        localStorage.removeItem('chatHistory');
        localStorage.removeItem('lastMessageTimestamp');
        history = [
            {
                role: 'bot',
                parts: [{ text: WELCOME_MESSAGE }],
                timestamp: new Date().toISOString(),
            },
        ];
    }

    return {
        history: history,
        isLoading: false,
        isStreaming: false, // New state for streaming status
        currentThought: '', // New state for displaying model's thinking process
        toolStatus: null, // New state for tool execution feedback { tool: string, status: 'running' | 'done' | 'error' }
        suggestions: [],
    };
};


export const useChatStore = defineStore('chat', {
  state: () => getInitialState(),
  actions: {
    async fetchSuggestions() {
      try {
        const response = await api.getSuggestions(this.history);
        if (response.data.suggestions) {
          this.suggestions = response.data.suggestions;
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        this.suggestions = []; // Clear suggestions on error
      }
    },
    async sendMessage(message) {
      const userMessage = { role: 'user', parts: [{ text: message }], timestamp: new Date().toISOString() };
      this.history.push(userMessage);
      this.suggestions = []; // Clear suggestions immediately
      this.isLoading = true;
      this.isStreaming = true;
      this.currentThought = '';
      this.toolStatus = null;
      
      // Create a placeholder bot message for streaming response
      const botMessageIndex = this.history.push({ 
          role: 'bot', 
          parts: [{ text: '' }], 
          timestamp: new Date().toISOString() 
      }) - 1;

      try {
        await api.streamChat(message, this.history.slice(0, -1), (event) => { // Send history excluding the current placeholder
            if (event.type === 'text') {
                this.history[botMessageIndex].parts[0].text += event.content;
                console.log('[Store] Text appended. Total length:', this.history[botMessageIndex].parts[0].text.length);
            } else if (event.type === 'thought') {
                this.currentThought += event.content;
            } else if (event.type === 'tool_start') {
                this.toolStatus = { tool: event.tool, status: 'running' };
            } else if (event.type === 'tool_end') {
                this.toolStatus = { ...this.toolStatus, status: 'done', result: event.result };
                // Reset after a short delay or keep it until next thought/text? 
                // Let's keep it visible until new text arrives or stream ends.
            } else if (event.type === 'done') {
                this.isLoading = false;
                this.isStreaming = false;
                this.toolStatus = null;
                localStorage.setItem('chatHistory', JSON.stringify(this.history));
                localStorage.setItem('lastMessageTimestamp', Date.now().toString());
                this.fetchSuggestions();
            } else if (event.type === 'error') {
                console.error('Streaming error:', event.content);
                if (!this.history[botMessageIndex].parts[0].text) {
                    this.history[botMessageIndex].parts[0].text = 'Désolé, une erreur est survenue pendant la génération.';
                }
                this.isLoading = false;
                this.isStreaming = false;
                this.toolStatus = null;
            }
        });

      } catch (error) { 
        console.error('Error initiating chat stream:', error);
        this.history[botMessageIndex].parts[0].text = 'Désolé, impossible de contacter le serveur.';
        this.isLoading = false;
        this.isStreaming = false;
        this.toolStatus = null;
      }
    },
    newConversation() {
      localStorage.removeItem('chatHistory');
      localStorage.removeItem('lastMessageTimestamp');
      this.$reset();
      // After reset, the state is recreated using getInitialState, which will provide the welcome message.
    },
  },
});